// The frame round every screen, laid out like grassickproperty.com.au: a grey app bar with the wordmark and search,
// the website's side menu on a computer (icons from 768px, labels from 1100px) with a right-hand column of the day
// at a glance, and on a phone a white tab bar with a raised blue Add button and a Menu sheet.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { api, onQueueChange, queuedActions, replayQueue } from "../lib/api.ts";
import { label, phone } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";
import { isDark, setTheme, ThemeSwitch } from "../lib/theme.tsx";
import { Wordmark } from "./Brand.tsx";
import { Icon } from "./Icon.tsx";
import { Avatar, useToast } from "./ui.tsx";

function useInboxCount() {
  const q = useQuery({ queryKey: ["inbox-count"], queryFn: () => api.get<any[]>("/conversations?filter=needs_reply&status=open&limit=200"), refetchInterval: 60_000 });
  return q.data?.length ?? 0;
}

/** Offline state and the queue of actions waiting to be sent. Replays when the signal comes back. */
function useOutbox() {
  const toast = useToast();
  const qc = useQueryClient();
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  useEffect(() => {
    const refresh = () => queuedActions().then((a) => setQueued(a.length));
    const up = async () => {
      setOnline(true);
      const r = await replayQueue();
      if (r.sent) { toast(`Back online: ${r.sent} saved change${r.sent > 1 ? "s" : ""} sent`); qc.invalidateQueries(); }
      if (r.failed) toast(`${r.failed} saved change${r.failed > 1 ? "s" : ""} couldn't be applied. See Account → Waiting to send.`, true);
    };
    const down = () => setOnline(false);
    refresh();
    const off = onQueueChange(refresh);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    const t = setInterval(() => navigator.onLine && replayQueue(), 30_000);
    if (navigator.onLine) replayQueue();
    return () => { off(); window.removeEventListener("online", up); window.removeEventListener("offline", down); clearInterval(t); };
  }, [toast, qc]);
  return { online, queued };
}

const roleName = (role: string) => (role === "agent" ? "Agent" : role === "admin" ? "Admin" : "Owner");
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ---------- search: people, properties, listings and deals (the API's /search) ----------

function GlobalSearch({ className = "", autoFocus, onPick }: { className?: string; autoFocus?: boolean; onPick?: () => void }) {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { const t = setTimeout(() => setTerm(text.trim()), 200); return () => clearTimeout(t); }, [text]);
  useEffect(() => {
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const r = useQuery({ queryKey: ["search", term], queryFn: () => api.get<any>(`/search?q=${encodeURIComponent(term)}`), enabled: term.length >= 2 });
  const go = (to: string) => { setText(""); setTerm(""); setOpen(false); onPick?.(); nav(to); };
  const d = r.data;
  const any = d && (d.people.length || d.properties.length || d.listings.length || d.deals.length);
  return (
    <div className={"gsearch " + className} ref={box}>
      <form className="gsearch__box" role="search" onSubmit={(e) => { e.preventDefault(); if (term.length >= 2) go(`/people?q=${encodeURIComponent(term)}`); }}>
        <Icon name="search" />
        <input type="search" value={text} placeholder="Search people, properties, listings" aria-label="Search the CRM" autoComplete="off" autoFocus={autoFocus}
          onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} />
      </form>
      {open && term.length >= 2 && (
        <div className="gsearch__res" role="listbox">
          {r.isLoading ? <div className="loading"><div className="spinner" /></div> : !any ? <div className="gsearch__none">Nothing matches “{term}”.</div> : <>
            {d.people.length > 0 && <div className="gsearch__h">People</div>}
            {d.people.map((p: any) => (
              <button key={p.id} className="row" onClick={() => go(`/people/${p.id}`)}>
                <Avatar name={p.display_name} sm />
                <div className="main-col"><div className="title">{p.display_name}</div><div className="sub">{p.mobile ? phone(p.mobile) : p.email ?? label(p.kind)}</div></div>
              </button>
            ))}
            {d.properties.length > 0 && <div className="gsearch__h">Properties</div>}
            {d.properties.map((p: any) => (
              <button key={p.id} className="row" onClick={() => go(`/properties/${p.id}`)}>
                <Icon name="home" /><div className="main-col"><div className="title">{p.address_display}</div><div className="sub">{p.suburb}</div></div>
              </button>
            ))}
            {d.listings.length > 0 && <div className="gsearch__h">Listings</div>}
            {d.listings.map((l: any) => (
              <button key={l.id} className="row" onClick={() => go(`/listings/${l.id}`)}>
                <Icon name="listing" /><div className="main-col"><div className="title">{l.address_display}</div><div className="sub">{[cap(label(l.state)), l.headline].filter(Boolean).join(" · ")}</div></div>
              </button>
            ))}
            {d.deals.length > 0 && <div className="gsearch__h">Deals</div>}
            {d.deals.map((o: any) => (
              <button key={o.id} className="row" onClick={() => go(`/pipelines?pipeline=${o.pipeline}&open=${o.id}`)}>
                <Icon name="pipeline" /><div className="main-col"><div className="title">{o.title}</div><div className="sub">{o.pipeline === "seller" ? "Seller deal" : "Buyer deal"}</div></div>
              </button>
            ))}
          </>}
        </div>
      )}
    </div>
  );
}

function SearchSheet({ onClose }: { onClose: () => void }) {
  useEffect(() => { const k = (e: KeyboardEvent) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="searchsheet" role="dialog" aria-modal="true" aria-label="Search">
      <div className="searchsheet__top"><GlobalSearch autoFocus onPick={onClose} /><button className="iconbtn" onClick={onClose} aria-label="Close search"><Icon name="close" /></button></div>
      <div className="searchsheet__body" />
    </div>
  );
}

// ---------- phone sheets ----------

function BottomSheet({ label: name, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => { const k = (e: KeyboardEvent) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={name}>
        <div className="grab" />
        <button className="iconbtn x" onClick={onClose} aria-label="Close">{<Icon name="close" />}</button>
        {children}
      </div>
    </div>
  );
}

// ---------- the frame ----------

export function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  const inbox = useInboxCount();
  const { online, queued } = useOutbox();
  const loc = useLocation();
  const [sheet, setSheet] = useState<null | "menu" | "add" | "search">(null);
  useEffect(() => setSheet(null), [loc.pathname, loc.search]);
  const staff = me.user.role !== "agent";
  const owner = me.user.role === "owner";
  const today = useQuery({ queryKey: ["today"], queryFn: () => api.get<any>("/today"), refetchInterval: 120_000 });
  const t = today.data;
  const drafts = t?.draftsWaiting ?? 0;

  const main = [
    { to: "/", icon: "today", label: "Today", end: true },
    { to: "/inbox", icon: "inbox", label: "Inbox", count: inbox },
    { to: "/people", icon: "people", label: "People" },
    { to: "/pipelines", icon: "pipeline", label: "Pipelines" },
    { to: "/tasks", icon: "tasks", label: "Tasks" },
  ];
  const property = [
    { to: "/listings", icon: "listing", label: "Listings" },
    { to: "/properties", icon: "home", label: "Properties" },
  ];
  const admin = [
    ...(staff ? [{ to: "/duplicates", icon: "duplicates", label: "Duplicates" }] : []),
    ...(owner ? [{ to: "/imports", icon: "import", label: "Import review" }, { to: "/settings", icon: "settings", label: "Settings" }] : []),
  ];
  const inMenu = ["/pipelines", "/tasks", "/listings", "/properties", "/duplicates", "/imports", "/settings", "/more"].some((p) => loc.pathname.startsWith(p));
  const navi = (n: { to: string; icon: string; label: string; end?: boolean; count?: number }, sm = false) => (
    <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => "navi" + (sm ? " navi--sm" : "") + (isActive ? " active" : "")} title={n.label}>
      <Icon name={n.icon} /><span>{n.label}</span>{!!n.count && <em className="count">{n.count > 99 ? "99+" : n.count}</em>}
    </NavLink>
  );

  return (
    <>
      <header className="appbar">
        <div className="appbar__in">
          <Link to="/" className="appbar__logo" aria-label="Grassick CRM, Today"><Wordmark /></Link>
          <GlobalSearch className="gsearch--bar" />
          <div className="appbar__actions">
            <button className="iconbtn appbar__m" onClick={() => setSheet("search")} aria-label="Search"><Icon name="search" /></button>
            <Link to="/more" className="iconbtn appbar__m" aria-label="Account"><Avatar name={me.user.name} sm /></Link>
            <button className="iconbtn appbar__theme" onClick={() => setTheme(isDark() ? "light" : "dark")} aria-label="Switch between light and dark">
              <Icon name="moon" className="i-moon" /><Icon name="sun" className="i-sun" />
            </button>
            <Link to="/more" className="appbar__me"><Avatar name={me.user.name} sm />{me.user.name.split(" ")[0]}</Link>
          </div>
        </div>
      </header>

      <div className="shell">
        <nav className="sidenav" aria-label="Main">
          <div className="sidenav__group">{main.map((n) => navi(n))}</div>
          <div className="sidenav__group"><div className="sidenav__label">Property</div>{property.map((n) => navi(n))}</div>
          {admin.length > 0 && <div className="sidenav__group"><div className="sidenav__label">Admin</div>{admin.map((n) => navi(n, true))}</div>}
        </nav>

        <main className="main">{children}</main>

        <aside className="rail" aria-label="Your day">
          <section className="rcard">
            <div className="rcard__h">Your day<small>{new Date().toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "Australia/Brisbane" })}</small></div>
            <Link to="/inbox?filter=needs_reply" className={"dayrow" + (inbox ? " hot" : "")}><span>Waiting on a reply</span><b className="num">{inbox}</b><small>Inbox</small></Link>
            <Link to="/tasks" className="dayrow"><span>Tasks due</span><b className="num">{t?.tasks.length ?? "–"}</b><small>{t ? `${t.tasks.filter((x: any) => x.overdue).length} late` : " "}</small></Link>
            <Link to="/" className="dayrow"><span>Appointments</span><b className="num">{t ? t.appointments.filter((a: any) => a.day_offset === 0).length : "–"}</b><small>Today</small></Link>
            <Link to="/more" className={"dayrow" + (drafts ? " hot" : "")}><span>Drafts waiting</span><b className="num">{drafts}</b><small>Sending is {me.sending}</small></Link>
          </section>
          <section className="rcard mecard">
            <Avatar name={me.user.name} />
            <div><b>{me.user.name}</b><small>{roleName(me.user.role)}, Grassick Property</small></div>
            <div className="btns"><Link className="btn white" to="/more">Account</Link><Link className="btn" to="/people?add=1">Add person</Link></div>
          </section>
          <footer className="railfoot">
            <div className="railtheme"><span>Appearance</span><ThemeSwitch /></div>
            <Link to="/more">Account</Link> · <a href="https://grassickproperty.com.au/" target="_blank" rel="noopener">Website</a>
            <p>Grassick CRM · sending {me.sending}</p>
          </footer>
        </aside>
      </div>

      <nav className="tabbar" aria-label="Main">
        <NavLink to="/" end className={({ isActive }) => "tab" + (isActive && !sheet ? " active" : "")}><Icon name="today" />Today</NavLink>
        <NavLink to="/inbox" className={({ isActive }) => "tab" + (isActive && !sheet ? " active" : "")}>
          <Icon name="inbox" />Inbox{!!inbox && <em className="count">{inbox > 99 ? "99+" : inbox}</em>}
        </NavLink>
        <button className="tab tab--add" onClick={() => setSheet("add")} aria-haspopup="dialog"><span className="tab__sq"><Icon name="plus" /></span>Add</button>
        <NavLink to="/people" className={({ isActive }) => "tab" + (isActive && !sheet ? " active" : "")}><Icon name="people" />People</NavLink>
        <button className={"tab" + (inMenu || sheet === "menu" ? " active" : "")} onClick={() => setSheet("menu")} aria-haspopup="dialog"><Icon name="menu" />Menu</button>
      </nav>

      {sheet === "menu" && (
        <BottomSheet label="Menu" onClose={() => setSheet(null)}>
          <Link to="/more" className="agentrow"><Avatar name={me.user.name} /><span><b>{me.user.name}</b><small>{roleName(me.user.role)}, Grassick Property</small></span></Link>
          <div className="menu-tiles">
            {[main[3], main[4], ...property, ...admin].map((n) => (
              <Link key={n.to} to={n.to} className={loc.pathname.startsWith(n.to) ? "on" : ""}><Icon name={n.icon} /><span>{n.label}</span></Link>
            ))}
          </div>
          <div className="menu-sec">Account</div>
          <div className="menu-links">
            <Link to="/more"><Icon name="lock" />Drafts waiting{drafts > 0 && <em className="count">{drafts}</em>}</Link>
            {queued > 0 && <Link to="/more"><Icon name="offline" />Waiting to send<em className="count">{queued}</em></Link>}
            <Link to="/more"><Icon name="user" />Account and sign out</Link>
          </div>
          <div className="menu-theme"><span>Appearance</span><ThemeSwitch /></div>
          <p className="legal">Grassick CRM · sending {me.sending}</p>
        </BottomSheet>
      )}

      {sheet === "add" && (
        <BottomSheet label="Add" onClose={() => setSheet(null)}>
          <h2>Add</h2>
          <div className="menu-links">
            <Link to="/people?add=1"><Icon name="user" />Add a person</Link>
            <Link to="/pipelines?add=1"><Icon name="pipeline" />New deal</Link>
            <Link to="/tasks?add=1"><Icon name="tasks" />New task</Link>
            <Link to="/properties?add=1"><Icon name="home" />Add a property</Link>
          </div>
        </BottomSheet>
      )}

      {sheet === "search" && <SearchSheet onClose={() => setSheet(null)} />}

      {(!online || queued > 0) && (
        <Link to="/more" className="offline-pill" role="status">
          {!online && <Icon name="offline" size={14} />}
          {!online ? "Offline" : "Sending"}{queued > 0 ? ` · ${queued} waiting` : ""}
        </Link>
      )}
    </>
  );
}
